import { NvButton } from '@simwood/nv-react-components';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
//FIXME
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBack } from '../../../app/components/icons';
import { smsMessageReceived } from '../../reducers/sms';
import { sendSmsMessage } from '../../services/smsService';
import { StoreDispatch, StoreState } from '../../store';
import { ApiSms, SmsThread } from '../../types/Sms';
import * as styles from './styles.module.scss';

interface Props {
  thread: SmsThread;
  compact?: boolean;
  onBack: () => void;
}

const getSmsCallerIdNumber = (sms: ApiSms) => {
  if (sms.direction === 'IN') {
    return sms.to;
  }
  return sms.from;
};

// assume that the thread is already sorted by created
const partitionThreadByDates = (messages: ApiSms[]) => {
  const partsById: Record<string, ApiSms[]> = {};
  const dateOrdering: string[] = [];
  messages.forEach((m) => {
    const date = DateTime.fromISO(m.created).toFormat('dd MMMM yyyy');
    if (!partsById[date]) {
      partsById[date] = [];
      dateOrdering.push(date);
    }
    partsById[date].push(m);
  });
  return dateOrdering.map((d) => ({ date: d, messages: partsById[d] }));
};

const SmsFeed: React.FC<Props> = ({ thread, compact, onBack }) => {
  const parts = useMemo(
    () => partitionThreadByDates([...thread.messages].reverse()),
    [thread],
  );
  const bottomRef = useRef<HTMLLIElement>(null);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const dispatch = useDispatch<StoreDispatch>();

  const customerId = useSelector(
    (state: StoreState) => state.client.activeCustomer?.id,
  );
  const latestMessageWithValidCallerId = useMemo(
    () =>
      thread.messages.find((m) => !!Number(getSmsCallerIdNumber(m))) ||
      thread.messages[thread.messages.length - 1],
    [thread.messages],
  );

  let senderNumber: string | undefined = undefined;
  if (latestMessageWithValidCallerId) {
    senderNumber = getSmsCallerIdNumber(latestMessageWithValidCallerId);
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [thread.messages.length]);

  if (!customerId) {
    return null;
  }

  const handleSendMessage = async () => {
    if (messageText.trim()) {
      try {
        setIsSending(true);
        const message = await sendSmsMessage(customerId, {
          to: thread.number,
          from: senderNumber || '',
          body: messageText.trim(),
        });
        dispatch(smsMessageReceived(message));

        setMessageText('');
      } catch (err) {
        // TODO
        console.error(err);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <div className={styles.header}>
        <NvButton size='large' borderless inverted onClick={onBack}>
          <ArrowBack />
        </NvButton>
        <h3>{thread.number}</h3>
      </div>

      <ul
        className={[styles.list, compact ? styles.compact : '']
          .join(' ')
          .trim()}
      >
        {parts.map((p) => (
          <li className={styles.listBlock} key={p.date}>
            <span className={styles.dateDivider}>{p.date}</span>
            <div className={styles.messageList}>
              {p.messages.map((m) => (
                <div
                  key={m.id}
                  className={[
                    styles.messageContainer,
                    m.direction === 'IN' ? styles.incoming : styles.out,
                  ].join(' ')}
                >
                  <div className={styles.message}>
                    <div className={styles.messageHeader}>
                      <div />
                      <div>{DateTime.fromISO(m.created).toFormat('HH:mm')}</div>
                    </div>
                    <div>{m.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </li>
        ))}

        <li className={styles.listBlock} ref={bottomRef} />
      </ul>

      <div className={styles.messageInputContainer}>
        <div className={styles.messageInputWrapper}>
          <textarea
            className={styles.messageInput}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder='Type your message...'
            disabled={isSending}
            rows={1}
          />
          <NvButton
            className={styles.sendButton}
            size='standard'
            onClick={handleSendMessage}
            disabled={!messageText.trim() || isSending || !senderNumber}
          >
            Send
          </NvButton>
        </div>
      </div>
    </>
  );
};

export default React.memo(SmsFeed);
